import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Country } from '../api/countries';

interface Props {
  countries: Country[];
  value: string; // phoneCode
  onChange: (phoneCode: string) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
}

const SearchableCountrySelect: FC<Props> = ({ countries, value, onChange, disabled, loading, error }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const options = useMemo(() => {
    const list = countries
      .filter((c) => Boolean(c.phoneCode))
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      c.name.toLowerCase().includes(q) || (c.iso2 ?? '').toLowerCase().includes(q) || (c.iso3 ?? '').toLowerCase().includes(q),
    );
  }, [countries, query]);

  const selected = useMemo(() => options.find((c) => c.phoneCode === value) || countries.find((c) => c.phoneCode === value) || null, [options, countries, value]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open) {
      // Focus search when opened
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery('');
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    const items = listRef.current?.querySelectorAll<HTMLButtonElement>('button[data-option="1"]');
    if (!items || items.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? Array.from(items).indexOf(active as HTMLButtonElement) : -1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = items[Math.min(items.length - 1, Math.max(0, idx + 1))];
      next?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = items[Math.max(0, Math.min(items.length - 1, idx - 1))];
      prev?.focus();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className="inline-flex w-64 items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-left">
          {loading ? 'Loading…' : error ? 'Codes unavailable' : selected ? `${selected.name}${selected.iso2 ? ` (${selected.iso2})` : ''}${selected.phoneCode ? ` - +${selected.phoneCode}` : ''}` : 'Select country'}
        </span>
        <svg className="ml-2 h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
        </svg>
      </button>

      {open && !loading && (
        <div className="absolute z-20 mt-1 w-[24rem] rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search country name or code…"
            className="mb-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div ref={listRef} role="listbox" className="max-h-64 overflow-auto">
            {options.length === 0 ? (
              <div className="px-2 py-4 text-center text-sm text-slate-500">No matches</div>
            ) : (
              options.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-option="1"
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-slate-50 ${c.phoneCode === value ? 'bg-primary/5' : ''}`}
                  onClick={() => {
                    onChange(c.phoneCode ?? '');
                    setOpen(false);
                  }}
                >
                  <span className="truncate">
                    {c.name}{c.iso2 ? ` (${c.iso2})` : ''}
                  </span>
                  <span className="ml-2 shrink-0 tabular-nums text-slate-500">{c.phoneCode ? `+${c.phoneCode}` : ''}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableCountrySelect;

